import { Trash2 } from "lucide-react";
import { useId } from "react";
import type { JSX, ReactNode } from "react";

import type {
  CalculatorLevel,
  FormColumn as ServiceFormColumn,
} from "../../services/calculatorService";
import { cx } from "../../utils/functions";
import { Alert } from "../ui/Alert";
import { Card } from "../ui/Card";
import { HelpIcon } from "../ui/HelpIcon";
import type { ExtendedFormRow } from "./formTypes";

/**
 * The add-row assessment table, shared by Procurement, Eco Office and anything
 * else whose schema serves columns with no static rows.
 *
 * Presentational only. Autosave, validation, option resolution and submit all
 * stay in Calculator and arrive as `renderField`, so this never becomes a
 * second copy of the matrix form engine.
 *
 * Everything assessment-specific is display copy and geometry, and arrives in
 * `config`. Callers wrap this with their own component rather than passing the
 * config at every call site.
 */

export interface AddRowTableConfig {
  /**
   * Column label to Tailwind twelfths. `table-fixed` scales every column down
   * until labels break mid-word unless these plus Remove total 12. Unread when
   * `layout` is `"cards"`, which never builds a table.
   */
  widths?: Record<string, string>;
  /**
   * Compact header text for the wide table only. The stacked cards keep the
   * full label: the abbreviations exist because twelve columns leave no room,
   * and a card has a whole row to itself.
   */
  headerLabels?: Record<string, string>;
  /**
   * Fallback help text for columns the backend sends with no description.
   *
   * A function, not a label-keyed record: Heavy Equipment serves two pairs of
   * columns with the same label and keys its copy by field key instead.
   */
  tooltipFor?: (column: ServiceFormColumn) => string | undefined;
  /** Columns whose values build a row's heading in the stacked layout. */
  headingColumns: string[];
  /** Shown instead of the table when there are no rows yet. */
  emptyNote: ReactNode;
  /** Optional progress line under the table. */
  footerNote?: ReactNode;
  /** What the add button says, naming the thing it adds. Defaults to "Add Row". */
  addLabel?: string;
  /**
   * A leading number column in the wide table, so a row named "Centrifuge 2"
   * elsewhere can be found. The cards already show the name as their heading.
   */
  showRowNumbers?: boolean;
  /**
   * Rows that must remain. Procurement needs five items, so its table must
   * never empty out; assessments with no minimum allow removing the last row
   * and fall back to `emptyNote`. Defaults to 1.
   */
  minRows?: number;
  /**
   * `"cards"` drops the wide table and stacks at every width. For schemas with
   * far more columns than a grid can hold, where the table is not a better
   * layout that happens not to fit. Defaults to `"table"`.
   */
  layout?: "table" | "cards";
  /**
   * Columns this row should not be asked at all, given its current answers.
   * Greying them is only honest when the cell is one of a handful; past that a
   * row of dashes is noise, so these are left out of the card.
   *
   * Values arrive keyed by column id, as `valuesOf` returns them.
   */
  hiddenFor?: (
    column: ServiceFormColumn,
    values: Record<string, string>,
  ) => boolean;
}

/**
 * What a caller must put on the control it renders, so the label beside it
 * actually points at something.
 *
 * Not optional: the label lives here and the control lives in `Calculator`, and
 * when nothing forced the two to meet, every field in the card layout went out
 * with no accessible name at all.
 */
export interface FieldA11y {
  /** Goes on the control. The card's `<label htmlFor>` targets it. */
  id: string;
  /** The help text's id, when the column has help text. */
  describedBy?: string;
  /**
   * The id of the column header's label, for the wide table only.
   *
   * The cards name a control with `<label htmlFor>`. A table cannot — the name
   * lives in the `<th>` — and `scope="col"` gives a screen reader column
   * context when navigating, not a name on the control itself. So the table
   * names each control from its header instead.
   */
  labelledBy?: string;
}

export interface AddRowTableProps {
  level: CalculatorLevel;
  columns: ServiceFormColumn[];
  rows: ExtendedFormRow[];
  renderField: (
    row: ExtendedFormRow,
    column: ServiceFormColumn,
    a11y: FieldA11y,
  ) => JSX.Element;
  /** Baseline lets rows be added and removed; savings inherits the baseline's
   * set and must not change it. */
  allowRemoveRows: boolean;
  onRemoveRow: (rowId: string) => void;
  locked: boolean;
  /** A row's current values by column id, for building row headings. */
  valuesOf: (rowId: string) => Record<string, string>;
  config: AddRowTableConfig;
}

/**
 * Tier styling for an assessment table.
 *
 * Silver has purpose-built surface and body-text tokens. Gold's `--l0-gold*`
 * tokens are brand tones, not a readable surface, and inventing one would mean
 * a new hex, so gold and bronze use daisyui's semantic base classes.
 */
const tierClasses = (
  level: string,
): { thead: string; heading: string; body: string } =>
  level === "silver"
    ? {
        thead: "thead-silver",
        heading: "text-silver-heading",
        body: "text-silver-body",
      }
    : {
        thead: "bg-base-200 text-base-content",
        heading: "text-base-content",
        body: "text-base-content/70",
      };

/** The card surface comes from the `Card` primitive, which already carries the
 * border and the silver token set. */
const cardVariant = (level: string): "silver" | undefined =>
  level === "silver" ? "silver" : undefined;

/**
 * The accepted range, as readable text.
 *
 * This used to be the input's placeholder, where it was grey text inside a
 * light border that read as a disabled field, and it disappeared the moment
 * anyone typed. It belongs with the help text, which stays put.
 */
const rangeHintFor = (
  constraints: ServiceFormColumn["constraints"] | undefined,
): string => {
  if (
    !constraints ||
    (constraints.type !== "integer_range" &&
      constraints.type !== "decimal_range")
  ) {
    return "";
  }
  const { min_value: min, max_value: max } = constraints;
  if (min !== undefined && max !== undefined)
    return `Accepts ${min} to ${max}.`;
  if (min !== undefined) return `Accepts ${min} or more.`;
  if (max !== undefined) return `Accepts up to ${max}.`;
  return "";
};

/**
 * Heading for a row in the stacked layout, built from whichever identity
 * columns the user has filled so far. An unnamed row still needs a heading, so
 * it falls back to its generated label.
 */
const rowHeadingFor = (
  row: ExtendedFormRow,
  columns: ServiceFormColumn[],
  valuesOf: (rowId: string) => Record<string, string>,
  headingColumns: string[],
): string => {
  const values = valuesOf(String(row.id));
  const parts = headingColumns
    .map((label) => columns.find((c) => c.label === label))
    .map((column) => (column ? values[String(column.id)] : ""))
    .filter((value) => value && value.trim() !== "");

  return parts.length > 0 ? parts.join(" · ") : row.label;
};

export const AddRowTable = ({
  level,
  columns,
  rows,
  renderField,
  allowRemoveRows,
  onRemoveRow,
  locked,
  valuesOf,
  config,
}: AddRowTableProps): JSX.Element => {
  // Heavy Equipment renders one table per equipment type and they share column
  // ids, so header ids need an instance prefix or they collide across groups.
  const instanceId = useId();
  const headerId = (column: ServiceFormColumn): string =>
    `${instanceId}-h-${column.id}`;
  const tier = tierClasses(level);
  const columnTip = (column: ServiceFormColumn): string =>
    [
      column.description || config.tooltipFor?.(column) || "",
      rangeHintFor(column.constraints),
    ]
      .filter(Boolean)
      .join(" ");
  const headingOf = (row: ExtendedFormRow): string =>
    rowHeadingFor(row, columns, valuesOf, config.headingColumns);
  const canRemove = rows.length > (config.minRows ?? 1);
  const stackedOnly = config.layout === "cards";
  const columnsFor = (row: ExtendedFormRow): ServiceFormColumn[] => {
    if (!config.hiddenFor) return columns;
    const values = valuesOf(String(row.id));
    return columns.filter((column) => !config.hiddenFor?.(column, values));
  };

  if (rows.length === 0) {
    return (
      <Alert variant="info" soft>
        <span>{config.emptyNote}</span>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* The table appears only at xl. Eight dense columns need roughly 1200px
          before labels start breaking mid-word, and scrolling that grid
          sideways is not a tablet layout — below xl the stacked cards are the
          real answer, not a fallback.

          Skipped outright under `layout: "cards"`, not just hidden: a hidden
          table still renders every field, which would duplicate the whole form
          in the accessibility tree. */}
      {!stackedOnly && (
        <div className="hidden xl:block">
          <table className="table table-fixed table-sm table-cells-fit w-full">
            <thead>
              <tr>
                {/* Blank, as the shared Table leaves its number column. */}
                {config.showRowNumbers && (
                  <th scope="col" className={cx(tier.thead, "w-10")} />
                )}
                {columns.map((column, index) => {
                  const tip = columnTip(column);
                  // A 20rem tip centred under an edge header spills off the
                  // page, so the edge columns open theirs inwards.
                  const placement =
                    index === 0
                      ? "tooltip-right"
                      : index >= columns.length - 2
                        ? "tooltip-left"
                        : "tooltip-bottom";
                  return (
                    <th
                      key={column.id}
                      scope="col"
                      // whitespace-normal overrides daisyui's `thead { white-space:
                      // nowrap }`. Under table-fixed a header that cannot wrap
                      // overflows its column and paints over the next one.
                      //
                      // Deliberately NOT overflow-hidden. That was here as a guard
                      // against an over-long header spilling into its neighbour,
                      // but the tooltip is anchored to the button inside this cell
                      // and clipping the cell clips the tooltip with it.
                      className={cx(
                        tier.thead,
                        "whitespace-normal align-bottom",
                        // Only when the caller sets widths. Heavy Equipment
                        // groups vary from 5 to 12 columns, so a fixed twelfth
                        // each would leave a narrow group claiming 5/12 of the
                        // table; with no class the browser distributes evenly.
                        config.widths?.[column.label],
                      )}
                    >
                      {/* Same label-and-icon header as the other assessment
                          tables. A button so the tip opens from the keyboard,
                          and font-normal so the tip doesn't inherit bold. */}
                      <div className="flex items-center gap-2 px-2 py-2">
                        <span
                          id={headerId(column)}
                          className="min-w-0 leading-tight break-words"
                        >
                          {config.headerLabels?.[column.label] ?? column.label}
                          {column.is_required && column.is_editable && (
                            <span className="ml-2 text-error" aria-hidden="true">
                              *
                            </span>
                          )}
                        </span>
                        {tip && (
                          <button
                            type="button"
                            className={cx(
                              "tooltip tooltip-custom cursor-help flex items-center font-normal",
                              placement,
                            )}
                            data-tip={tip}
                            aria-label={`${column.label}. ${tip}`}
                          >
                            <HelpIcon />
                          </button>
                        )}
                      </div>
                    </th>
                  );
                })}
                {allowRemoveRows && (
                  <th
                    scope="col"
                    className={cx(
                      tier.thead,
                      "w-1/12 whitespace-normal px-2 py-2 align-bottom",
                    )}
                  >
                    Remove
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  {config.showRowNumbers && (
                    <th scope="row">
                      <span aria-hidden="true">{index + 1}</span>
                      <span className="sr-only">{row.label}</span>
                    </th>
                  )}
                  {columns.map((column) => (
                    <td
                      key={`${row.id}-${column.id}`}
                      className="p-1 align-middle"
                    >
                      {renderField(row, column, {
                        id: `f-${row.id}-${column.id}`,
                        labelledBy: headerId(column),
                      })}
                    </td>
                  ))}
                  {allowRemoveRows && (
                    <td className="p-1 align-middle">
                      <button
                        type="button"
                        disabled={locked || !canRemove}
                        className="btn btn-ghost btn-sm text-error"
                        onClick={() => onRemoveRow(String(row.id))}
                        aria-label={`Remove ${headingOf(row)}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* One card per row, everywhere below xl.

          list-none is load-bearing: App.css sets `ul { list-disc pl-6 }` over
          every list in the app, so without it these cards carry a bullet. */}
      <ul
        className={cx("list-none space-y-4 pl-0", !stackedOnly && "xl:hidden")}
      >
        {rows.map((row) => {
          const shown = columnsFor(row);
          // The heading repeats whichever identity columns are filled, so it is
          // noise when the card already shows those fields. Cards-only
          // assessments always do; the table layout does not.
          const headingIsRedundant = config.headingColumns.every((label) =>
            shown.some((column) => column.label === label),
          );
          return (
            <li key={row.id}>
              <Card variant={cardVariant(level)} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h4 className={cx("text-sm font-semibold", tier.heading)}>
                    {headingIsRedundant ? row.label : headingOf(row)}
                  </h4>
                  {allowRemoveRows && (
                    <button
                      type="button"
                      disabled={locked || !canRemove}
                      className="btn btn-ghost btn-sm min-h-11 text-error"
                      onClick={() => onRemoveRow(String(row.id))}
                      aria-label={`Remove ${headingOf(row)}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
                {/* Answering a question can remove others from the card. Sighted
                    users see that happen; this is the only signal anyone else
                    gets. */}
                <p className="sr-only" role="status">
                  {`${shown.length} question${shown.length === 1 ? "" : "s"}`}
                </p>
                {/* A grid, not a stack. These cards started out as the
                    below-xl layout where one field per line is right; a
                    cards-only assessment renders them at full desktop width
                    too, where a stack leaves a two-digit input metres wide. */}
                <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
                  {shown.map((column) => {
                    const fieldId = `f-${row.id}-${column.id}`;
                    const tip = columnTip(column);
                    const tipId = tip ? `${fieldId}-tip` : undefined;
                    // The column that decides what the rest of the row asks
                    // leads, on its own line.
                    const spansRow = config.headingColumns.includes(
                      column.label,
                    );
                    return (
                      <div
                        key={fieldId}
                        className={cx(
                          "flex flex-col",
                          spansRow && "sm:col-span-2 xl:col-span-3",
                        )}
                      >
                        <dt>
                          <label
                            htmlFor={fieldId}
                            className={cx(
                              "text-xs font-semibold",
                              tier.heading,
                            )}
                          >
                            {column.label}
                            {column.is_required && column.is_editable && (
                              <span className="text-error" aria-hidden="true">
                                {" *"}
                              </span>
                            )}
                          </label>
                        </dt>
                        {tip && (
                          <p
                            id={tipId}
                            className={cx(
                              "mt-1 max-w-prose text-xs",
                              tier.body,
                            )}
                          >
                            {tip}
                          </p>
                        )}
                        <dd className="mt-auto pt-2">
                          {renderField(row, column, {
                            id: fieldId,
                            describedBy: tipId,
                          })}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </Card>
            </li>
          );
        })}
      </ul>

      {config.footerNote && (
        <p className={cx("text-xs", tier.body)}>{config.footerNote}</p>
      )}
    </div>
  );
};
